import { AfterContentInit, AfterViewInit, Directive, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { IUIElementProps, UIElement } from './UIElement';

/**
 * A property interface of all properties that contained in the {@link FrameworkElement}.
 * Extends the {@link IUIElementProps} interface.
 * @public
 */
export interface IFrameworkElementProps<TElement extends HTMLElement = HTMLElement> extends IUIElementProps<TElement> {
  readonly isViewInitialized: boolean;
  readonly isContentInitialized: boolean;
  readonly init: EventEmitter<FrameworkElement>;
  readonly contentInit: EventEmitter<FrameworkElement>;
  readonly viewInit: EventEmitter<FrameworkElement>;
  readonly destroy: EventEmitter<FrameworkElement>;
}

/**
 * todo doc
 *
 * Extends the {@link UIElement} class.
 * Implements the {@link @angular/core#OnInit}, {@link @angular/core#OnDestroy}, {@link @angular/core#AfterContentInit}, {@link @angular/core#AfterViewInit} and {@link IFrameworkElementProps} interface
 *
 * @public
 * @dynamic
 */
@Directive()
export abstract class FrameworkElement<TElement extends HTMLElement = HTMLElement>
  extends UIElement<TElement>
  implements OnInit, OnDestroy, AfterContentInit, AfterViewInit, IFrameworkElementProps<TElement>
{
  private _isViewInitialized: boolean;
  private _isContentInitialized: boolean;
  private readonly _init: EventEmitter<FrameworkElement>;
  private readonly _contentInit: EventEmitter<FrameworkElement>;
  private readonly _viewInit: EventEmitter<FrameworkElement>;
  private readonly _destroy: EventEmitter<FrameworkElement>;

  public constructor(nativeElement: TElement) {
    super(nativeElement);

    this._isViewInitialized = false;
    this._isContentInitialized = false;
    this._init = new EventEmitter<FrameworkElement>();
    this._contentInit = new EventEmitter<FrameworkElement>();
    this._viewInit = new EventEmitter<FrameworkElement>();
    this._destroy = new EventEmitter<FrameworkElement>();
  }

  /**
   * Gets the value of the angular lifecicle `AfterViewInit` has been called.
   * @protected
   * @default false
   */
  public get isViewInitialized(): boolean {
    return this._isViewInitialized;
  }

  /**
   * Gets the value of the angular lifecicle `AfterContentInit` has been called.
   * @protected
   * @default false
   */
  public get isContentInitialized(): boolean {
    return this._isContentInitialized;
  }

  /**
   * Called when the {@link FrameworkElement} is initialized.
   * Provides reference to the {@link FrameworkElement} as event argument.
   * @public
   * @eventProperty
   * @readonly
   * @type EventEmitter<FrameworkElement>
   * @default EventEmitter
   */
  @Output()
  public get init(): EventEmitter<FrameworkElement> {
    return this._init;
  }

  /**
   * Called when the {@link FrameworkElement} content is initialized after `init` event.
   * Provides reference to the {@link FrameworkElement} as event argument.
   * @public
   * @eventProperty
   * @readonly
   * @type EventEmitter<FrameworkElement>
   * @default EventEmitter
   */
  @Output()
  public get contentInit(): EventEmitter<FrameworkElement> {
    return this._contentInit;
  }

  /**
   * Called when the {@link FrameworkElement} view is initialized after `contentInit`.
   * Provides reference to the {@link FrameworkElement} as event argument.
   * @public
   * @eventProperty
   * @readonly
   * @type EventEmitter<FrameworkElement>
   * @default EventEmitter
   */
  @Output()
  public get viewInit(): EventEmitter<FrameworkElement> {
    return this._viewInit;
  }

  /**
   * Called when the {@link FrameworkElement} is destroyed.
   * Provides reference to the {@link FrameworkElement} as event argument.
   * @public
   * @readonly
   * @type EventEmitter<FrameworkElement>
   * @default EventEmitter
   */
  @Output()
  public get destroy(): EventEmitter<FrameworkElement> {
    return this._destroy;
  }

  /**
   * @hidden
   * @public
   * @virtual
   */
  public ngOnInit(): void {
    this._init.emit(this);
  }

  /**
   * @hidden
   * @public
   * @virtual
   */
  public ngAfterContentInit(): void {
    this._isContentInitialized = true;
    this._contentInit.emit(this);
  }

  /**
   * @hidden
   * @public
   * @virtual
   */
  public ngAfterViewInit(): void {
    this._isViewInitialized = true;
    this._viewInit.emit(this);
  }

  /**
   * @hidden
   * @public
   * @virtual
   */
  public ngOnDestroy(): void {
    this._destroy.emit(this);

    this._isContentInitialized = false;
    this._isViewInitialized = false;
  }
}
